import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CaretLeft, CaretRight, PlusCircle, GearSix, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AppointmentTypeChooserModal from "@/components/AppointmentTypeChooserModal";
import QuickCreateAppointmentModal from "@/components/QuickCreateAppointmentModal";
import SharedAppointmentModal from "@/components/SharedAppointmentModal";
import CalendarSettingsDialog from "@/components/CalendarSettingsDialog";
import {
  flattenAgenda, timeLabel, kindMeta, dateKey, sameDay, buildMonthGrid, WD_SHORT, MONTHS_IT,
} from "@/pages/ProCalendarPage";

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dayLabel = (d) => d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const stop = (fn) => (ev) => { ev.stopPropagation(); fn(); };
const canDrag = (e) => e.kind === "personal" && !e.readonly;
const isPastDay = (d) => d < new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
const DRAG_MIME = "application/x-housetag-event";
const CAL_OPERATOR_ROLES = ["tech_operator", "desk_operator", "accounting_operator"];

function NavArrows({ onPrev, onNext, dark, prefix }) {
  const base = dark ? "text-slate-300 hover:bg-white/10" : "text-slate-700 hover:bg-slate-100";
  return (
    <div className="flex items-center justify-between gap-1">
      <button type="button" onClick={stop(onPrev)} className={`p-1 rounded ${base}`} aria-label="Precedente" data-testid={`${prefix}-prev`}>
        <CaretLeft size={14} weight="bold" />
      </button>
      <button type="button" onClick={stop(onNext)} className={`p-1 rounded ${base}`} aria-label="Successivo" data-testid={`${prefix}-next`}>
        <CaretRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function EventChip({ e, onOpen, compact, draggable }) {
  const meta = kindMeta(e.kind);
  const cls = e.is_pending_multi ? "bg-amber-50 border-amber-200 text-amber-800" : meta.color;
  const Tag = onOpen ? "button" : "div";
  const drag = draggable && canDrag(e);
  return (
    <Tag
      {...(onOpen ? { type: "button", onClick: (ev) => { ev.stopPropagation(); onOpen(e); } } : {})}
      {...(drag ? {
        draggable: true,
        onDragStart: (ev) => { ev.dataTransfer.setData(DRAG_MIME, e.id); ev.dataTransfer.effectAllowed = "move"; },
      } : {})}
      className={`block w-full truncate text-left leading-tight rounded border ${onOpen ? "hover:brightness-95" : ""} ${drag ? "cursor-grab active:cursor-grabbing" : ""} ${compact ? "text-[10px] px-1 py-0.5" : "text-xs px-1.5 py-1"} ${cls}`}
      title={`${e.title}${e.property_name ? ` — ${e.property_name}` : ""}${drag ? " · trascina per spostare" : ""}`}
      data-testid={`sidebar-cal-event-${e.id}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${e.is_pending_multi ? "bg-amber-500" : meta.dot}`} />
      {e.all_day ? "" : `${timeLabel(e)} `}{e.title}
    </Tag>
  );
}

function DayCell({ d, month, list, selected, onPickDay, onCreate, onMove }) {
  const [hover, setHover] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const k = dateKey(d);
  const inMonth = d.getMonth() === month.getMonth();
  const droppable = !isPastDay(d);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onPickDay(d)}
      onKeyDown={(ev) => { if (ev.key === "Enter") onPickDay(d); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragOver={(ev) => {
        if (!droppable || !ev.dataTransfer.types.includes(DRAG_MIME)) return;
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "move";
        setDropOver(true);
      }}
      onDragLeave={() => setDropOver(false)}
      onDrop={(ev) => {
        ev.preventDefault();
        setDropOver(false);
        const id = ev.dataTransfer.getData(DRAG_MIME);
        if (id) onMove(id, d);
      }}
      className={`relative border-r border-b p-1 flex flex-col min-h-[84px] cursor-pointer transition-colors ${dropOver ? "bg-purple-100 ring-2 ring-inset ring-purple-500" : selected ? "bg-purple-50 ring-1 ring-inset ring-purple-400" : inMonth ? "bg-white hover:bg-purple-50/60 text-slate-800" : "bg-slate-50/70 text-slate-300"}`}
      data-testid={`sidebar-cal-cell-${k}`}
    >
      <span className={`text-xs font-semibold self-end ${sameDay(d, new Date()) ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white" : ""}`}>
        {d.getDate()}
      </span>
      {inMonth && (
        <div className="mt-1 w-full space-y-0.5 overflow-hidden pb-4">
          {list.slice(0, 3).map((e) => <EventChip key={e.id} e={e} compact draggable />)}
          {list.length > 3 && <div className="text-[9px] font-bold text-slate-500 pl-1">+{list.length - 3} altri</div>}
        </div>
      )}
      {hover && (
        <button
          type="button"
          onClick={stop(() => onCreate(d))}
          className="absolute bottom-1 right-1 rounded-full text-purple-600 hover:text-purple-800 hover:scale-110 transition-transform"
          aria-label={`Nuovo appuntamento il ${d.getDate()}`}
          data-testid={`sidebar-cal-cell-add-${k}`}
        >
          <PlusCircle size={18} weight="fill" />
        </button>
      )}
    </div>
  );
}

function DayPanel({ day, list, onOpen, onCreate, onDelete, canDelete }) {
  return (
    <aside className="lg:w-64 shrink-0 rounded-lg border bg-slate-50 p-3 space-y-2" data-testid="sidebar-cal-day-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-900 capitalize">{dayLabel(day)}</div>
        <button type="button" onClick={() => onCreate(day)} className="text-purple-600 hover:text-purple-800" aria-label="Nuovo appuntamento" data-testid="sidebar-cal-panel-add">
          <PlusCircle size={18} weight="fill" />
        </button>
      </div>
      {list.length ? (
        <div className="space-y-1" data-testid="sidebar-cal-panel-list">
          {list.map((e) => (
            <div key={e.id} className="flex items-center gap-1">
              <div className="flex-1 min-w-0"><EventChip e={e} onOpen={onOpen} draggable /></div>
              {canDelete(e) && (
                <button
                  type="button"
                  onClick={() => onDelete(e)}
                  className="shrink-0 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                  aria-label="Elimina"
                  title="Elimina"
                  data-testid={`sidebar-cal-panel-delete-${e.id}`}
                >
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-500" data-testid="sidebar-cal-panel-empty">Nessun appuntamento</div>
      )}
    </aside>
  );
}

function MonthGrid({ month, eventsByDay, selectedKey, onPickDay, onCreate, onMove }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold text-slate-500 uppercase border-b">
        {WD_SHORT.map((w) => <div key={w} className="py-1.5">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 border-l" data-testid="sidebar-cal-grid">
        {buildMonthGrid(month.getFullYear(), month.getMonth()).map((d) => (
          <DayCell key={dateKey(d)} d={d} month={month} list={eventsByDay.get(dateKey(d)) || []}
            selected={dateKey(d) === selectedKey} onPickDay={onPickDay} onCreate={onCreate} onMove={onMove} />
        ))}
      </div>
    </div>
  );
}

function DayList({ list }) {
  if (!list.length) {
    return <div className="text-[11px] text-slate-400 py-2" data-testid="sidebar-cal-empty">Nessun evento</div>;
  }
  return (
    <div className="space-y-1 max-h-48 overflow-y-auto no-scrollbar" data-testid="sidebar-cal-day-list">
      {list.map((e) => <EventChip key={e.id} e={e} />)}
    </div>
  );
}

export default function SidebarCalendar() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isOperator = CAL_OPERATOR_ROLES.includes(user?.role);
  const [events, setEvents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [calDate, setCalDate] = useState(() => new Date());
  const [monthOpen, setMonthOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [createDate, setCreateDate] = useState(() => new Date());
  const [chooserOpen, setChooserOpen] = useState(false);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [sharedCreateOpen, setSharedCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/me/agenda")
      .then((r) => setEvents(flattenAgenda(r.data || {})))
      .catch(() => setEvents([]));
  }, []);
  useEffect(() => {
    load();
    api.get("/properties", { params: isOperator ? { assigned_to_me: true } : { archived: false } })
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]));
  }, [load, isOperator]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      if (!m.has(e.dateKey)) m.set(e.dateKey, []);
      m.get(e.dateKey).push(e);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.dt - b.dt);
    return m;
  }, [events]);

  const openMonth = () => {
    setMonth(new Date(calDate.getFullYear(), calDate.getMonth(), 1));
    setSelectedDay(calDate);
    setMonthOpen(true);
  };
  const openEvent = (e) => {
    setMonthOpen(false);
    if (isOperator) { if (e.practice_id) nav(`/practices/${e.practice_id}`); return; }
    nav(e.property_id ? `/properties/${e.property_id}` : "/pro-calendar");
  };
  const startCreate = (d) => {
    if (properties.length === 0) {
      toast.info(isOperator
        ? "Nessun immobile tra le pratiche assegnate a te"
        : "Aggiungi prima almeno un immobile per creare un appuntamento");
      return;
    }
    setCreateDate(d);
    if (isOperator) setQuickCreateOpen(true);
    else setChooserOpen(true);
  };
  const onChooseKind = (kind) => {
    setChooserOpen(false);
    if (kind === "personal") setQuickCreateOpen(true);
    else setSharedCreateOpen(true);
  };
  const initialDate = new Date(`${dateKey(createDate)}T10:00:00`);

  const canDelete = (e) => e.kind !== "site_visit" && !e.readonly && !(isOperator && e.kind === "deadline");
  const deleteEvent = async (e) => {
    const msg = e.kind === "deadline"
      ? `Eliminare "${e.title}" dal calendario? La scadenza resta sull'immobile.`
      : `Eliminare l'appuntamento "${e.title}"? L'operazione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(e.kind === "deadline" ? `/me/agenda/deadlines/${e.id}` : `/appointments/${e.id}`);
      setEvents((list) => list.filter((x) => x.id !== e.id));
      toast.success(e.kind === "deadline" ? "Scadenza rimossa dal calendario" : "Appuntamento eliminato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const moveEvent = async (id, day) => {
    const ev = events.find((x) => x.id === id);
    if (!ev || !canDrag(ev) || sameDay(ev.dt, day)) return;
    if (isPastDay(day)) { toast.error("Non puoi spostare un appuntamento in una data passata"); return; }
    const dt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), ev.dt.getHours(), ev.dt.getMinutes());
    const prev = events;
    setEvents((list) => list.map((x) => (x.id === id ? { ...x, dt, dateKey: dateKey(dt), start_at: dt.toISOString() } : x)));
    try {
      await api.put(`/appointments/${id}`, { slots: [{ start_at: dt.toISOString(), duration_min: ev.duration_min || 60 }] });
      toast.success(`"${ev.title}" spostato a ${dayLabel(dt)}`);
      setSelectedDay(day);
      load();
    } catch (err) {
      setEvents(prev);
      toast.error(err?.response?.data?.detail || "Spostamento non riuscito");
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openMonth}
      onKeyDown={(ev) => { if (ev.key === "Enter" && ev.target === ev.currentTarget) openMonth(); }}
      className="shrink-0 mx-3 mb-3 p-2.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer transition-colors"
      title="Apri calendario mensile"
      data-testid="sidebar-calendar"
    >
      <div className="flex items-center justify-between">
        <button type="button" onClick={stop(() => setSettingsOpen(true))} className="p-1 -ml-1 rounded text-slate-400 hover:text-white hover:bg-white/10" aria-label="Impostazioni calendario" data-testid="sidebar-cal-settings">
          <GearSix size={15} />
        </button>
        <div className="w-16">
          <NavArrows onPrev={() => setCalDate((d) => addDays(d, -1))} onNext={() => setCalDate((d) => addDays(d, 1))} dark prefix="sidebar-cal" />
        </div>
      </div>
      <div className="mt-1.5 text-sm font-bold text-white capitalize truncate" data-testid="sidebar-cal-date">{dayLabel(calDate)}</div>
      <div className="mt-1.5">
        <DayList list={eventsByDay.get(dateKey(calDate)) || []} />
      </div>
      <div className="flex justify-end -mb-1 -mr-1">
        <button type="button" onClick={stop(() => startCreate(calDate))} className="p-1 rounded-full text-purple-300 hover:text-white hover:bg-white/10" aria-label="Nuovo appuntamento" data-testid="sidebar-cal-add">
          <PlusCircle size={20} />
        </button>
      </div>

      <div onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()} className="contents">
        <Dialog open={monthOpen} onOpenChange={setMonthOpen}>
          <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="sidebar-cal-dialog">
            <DialogHeader>
              <DialogTitle className="capitalize" data-testid="sidebar-cal-dialog-title">{MONTHS_IT[month.getMonth()]} {month.getFullYear()}</DialogTitle>
              <DialogDescription className="sr-only">Calendario appuntamenti</DialogDescription>
            </DialogHeader>
            <div className="w-16">
              <NavArrows
                onPrev={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                onNext={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                prefix="sidebar-cal-dialog"
              />
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <MonthGrid month={month} eventsByDay={eventsByDay} selectedKey={dateKey(selectedDay)}
                onPickDay={setSelectedDay} onCreate={startCreate} onMove={moveEvent} />
              <DayPanel day={selectedDay} list={eventsByDay.get(dateKey(selectedDay)) || []} onOpen={openEvent} onCreate={startCreate} onDelete={deleteEvent} canDelete={canDelete} />
            </div>
          </DialogContent>
        </Dialog>

        <CalendarSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        <AppointmentTypeChooserModal open={chooserOpen} onClose={() => setChooserOpen(false)} onPick={onChooseKind} />
        <QuickCreateAppointmentModal
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          properties={properties}
          initialDate={initialDate}
          onCreated={() => { setQuickCreateOpen(false); load(); }}
          hidePropertyPicker={!isOperator}
        />
        <SharedAppointmentModal
          open={sharedCreateOpen && !isOperator}
          onClose={() => setSharedCreateOpen(false)}
          properties={properties}
          initialDate={initialDate}
          onCreated={() => { load(); }}
        />
      </div>
    </div>
  );
}
